import angular from "angular";
import { CaseStatusEnum, Deal, ProductFamilyEnum, ProductTypeEnum } from "@src/apis/ModularClient";
import { CompareResultDTO } from "@js/ui-dto/CompareResultsDto";

export class CompareResultsService {
    static $inject = ["$window"];
    private readonly STORAGE_KEY = "LoanCriteria:CompareResults";

    constructor(private $window: Window) { }

    create(partial?: Partial<CompareResultDTO>): CompareResultDTO {
        const defaults: CompareResultDTO = {
            dealId: 0,
            productFamily: ProductFamilyEnum.None,
            productType: ProductTypeEnum.None,
            dealStatus: CaseStatusEnum.Search
        };
        return Object.assign(this.clone(defaults), this.clone(partial ?? {}));
    }

    /** Map heavier backend model to slim DTO */
    mapDealToCompareDto(deal: Deal): CompareResultDTO {
        return this.create({
            dealId: deal.id,
            productFamily: deal.productFamily,
            productType: deal.productType,
            dealStatus: deal.status
        });
    }

    validate(dto: CompareResultDTO): string[] {
        const errs: string[] = [];
        if (!Number.isFinite(dto.dealId) || dto.dealId <= 0) errs.push("dealId must be a positive number.");
        if (dto.productFamily == null) errs.push("productFamily is required.");
        if (dto.productType == null) errs.push("productType is required.");
        if (dto.dealStatus == null) errs.push("dealStatus is required.");
        return errs;
    }

    toStorage(dto: CompareResultDTO): string {
        return JSON.stringify(dto);
    }

    fromStorage(json: string | null): CompareResultDTO {
        if (!json) return this.create();
        try {
            const parsed = JSON.parse(json);
            const v = parsed?.__v ?? 0;
            let data = parsed?.data ?? parsed;

            return this.create(data as Partial<CompareResultDTO>);
        } catch {
            return this.create();
        }
    }

    saveDealToSessionStorage(deal: Deal) {
        var compareDto = this.mapDealToCompareDto(deal);
        this.saveToSessionStorage(compareDto);
    }
    saveToSessionStorage(dto: CompareResultDTO): void {
        const errs = this.validate(dto);
        if (errs.length) throw new Error(`CompareResultDTO invalid: ${errs.join("; ")}`);

        const payload = this.toStorage(dto);

        try {
            this.$window.sessionStorage.setItem(this.STORAGE_KEY, payload);
        } catch (e: any) {
            if (e?.name === "QuotaExceededError") {
                this.$window.sessionStorage.removeItem(this.STORAGE_KEY);
                this.$window.sessionStorage.setItem(this.STORAGE_KEY, payload);
            } else {
                throw e;
            }
        }
    }

    load(): CompareResultDTO {
        const json = this.$window.sessionStorage.getItem(this.STORAGE_KEY);
        return this.fromStorage(json);
    }

    exists(): boolean {
        return this.$window.sessionStorage.getItem(this.STORAGE_KEY) !== null;
    }

    clear(): void {
        this.$window.sessionStorage.removeItem(this.STORAGE_KEY);
    }

    // util
    private clone<T>(x: T): T {
        return JSON.parse(JSON.stringify(x));
    }
}
